import axios from 'axios';
import { apiBase, authToken } from '../api/apiBase';
import { PHASES_RETAINED_READ_TIMEOUT_MS } from './mackPhasesEvidence';

export const USD_GDPNOW_FIELD_ID = 'mack.input.visible.USD.04';
export const USD_GDP_RETAINED_SOURCES = {
  rate: { owner: 'Phases', label: 'Published USD GDP growth rate', route: '/mack/phases/history',
    field_id: 'mack.phases.raw.USD.real_gdp_qoq_rate', origin: 'PHASES_DIRECT_SOURCE',
    series: 'A191RL1Q225SBEA', unit: 'percent quarter-on-quarter at annual rate' },
  level: { owner: 'Calculator', label: 'Published USD real GDP level', route: '/mack/calculator/history',
    field_id: 'mack.calculator.raw.USD.real_gdp_level', origin: 'CALCULATOR_DIRECT_SOURCE',
    series: 'GDPC1', unit: 'billions of chained 2017 USD; SAAR' },
} as const;
export type UsdGdpEvidenceKind = keyof typeof USD_GDP_RETAINED_SOURCES;
export interface UsdGdpHistoryRow extends Record<string, unknown> {
  field_id: string; unit: string; reference_period: string; publication_at: string | null;
  retrieved_at?: string | null; publisher: string; source_url: string;
}
export interface UsdGdpHistoryPage extends Record<string, unknown> {
  success: true; field_id: string; origin: string; rows: UsdGdpHistoryRow[];
  total: number; next_offset: number | null;
}
export class UsdGdpEvidenceResponseError extends Error {
  constructor() { super('Retained USD GDP evidence identity could not be verified'); this.name = 'UsdGdpEvidenceResponseError'; }
}
export class UsdGdpEvidenceTimeoutError extends Error {
  constructor() { super('Retained USD GDP evidence read timed out'); this.name = 'UsdGdpEvidenceTimeoutError'; }
}
const count = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
export function decodeUsdGdpHistory(raw: unknown, kind: UsdGdpEvidenceKind, offset = 0): UsdGdpHistoryPage {
  const source = USD_GDP_RETAINED_SOURCES[kind];
  const page = raw as UsdGdpHistoryPage;
  if (!source || !page || page.success !== true || page.field_id !== source.field_id || page.origin !== source.origin ||
      !count(page.total) || !Array.isArray(page.rows) ||
      (page.source_fetches !== undefined && page.source_fetches !== 0) ||
      (page.writer_entries !== undefined && page.writer_entries !== 0) ||
      (page.formula_execution !== undefined && page.formula_execution !== false) ||
      (page.next_offset !== null && (!count(page.next_offset) || page.next_offset <= offset)) ||
      page.rows.some(row => {
        const series = kind === 'rate' ? row?.series_identity : typeof row?.series_id === 'string'
          ? row.series_id : (row?.series_id as Record<string, unknown> | null)?.value;
        const value = row?.source_native_value ?? row?.native_value ?? row?.value;
        return !row || row.field_id !== source.field_id || series !== source.series || row.unit !== source.unit ||
          !nonempty(row.reference_period) || !nonempty(row.publisher) || !nonempty(row.source_url) ||
          !/^https?:\/\//.test(row.source_url) ||
          (row.publication_at !== null && typeof row.publication_at !== 'string') ||
          !['string', 'number'].includes(typeof value) || (typeof value === 'number' && !Number.isFinite(value));
      })) throw new UsdGdpEvidenceResponseError();
  return page;
}

export async function readUsdGdpHistory(kind: UsdGdpEvidenceKind, token?: string | null,
  signal?: AbortSignal, offset = 0): Promise<UsdGdpHistoryPage> {
  const source = USD_GDP_RETAINED_SOURCES[kind];
  if (!source || !count(offset)) throw new UsdGdpEvidenceResponseError();
  const response = await axios.get(`${apiBase()}${source.route}`, {
    params: { field_id: source.field_id, origin: source.origin, offset, limit: offset === 0 ? 1 : 30 },
    headers: { Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache' },
    signal, timeout: PHASES_RETAINED_READ_TIMEOUT_MS,
  });
  return decodeUsdGdpHistory(response.data, kind, offset);
}

export function usdGdpReadFailure(error: unknown): string {
  if (error instanceof UsdGdpEvidenceTimeoutError || (axios.isAxiosError(error) && ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code || ''))) {
    return 'The retained GDP read timed out. Refresh this supporting evidence to read it again.';
  }
  if (error instanceof UsdGdpEvidenceResponseError) return 'The retained GDP identity could not be verified. The supporting value is withheld.';
  if (axios.isAxiosError(error) && error.response?.status === 401) return 'Sign in again to read retained GDP evidence.';
  if (axios.isAxiosError(error) && error.response?.status === 403) return 'This session cannot read the retained GDP evidence.';
  return 'Retained GDP evidence could not be read. No source acquisition was requested.';
}
